import React from "react";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "types/store";

import Select from "react-select";

import { HexColorPicker } from "react-colorful";

import { Alert } from "react-bootstrap";

import { Button } from "aible";

import LabeledToggle from "components/Inputs/Toggle/Toggle";
import { LineInput } from "components/Inputs/LineInput";
import Tooltip from "components/Inputs/Tooltip";

import * as chatGPTReducer from "containers/ChatGPT/reducer";
import * as chatAppReducer from "containers/ChatApp/reducer";
import * as nextReducer from "containers/Next/NextReducer";
import * as viewReducer from "containers/View/reducer";
import * as homeReducer from "containers/Home/reducer";
import * as foldersReducer from "containers/Folders/reducer";
import * as languageModelArtifactReducer from "containers/LanguageModelArtifact/reducer";
import { createArtifact } from "containers/LanguageModelArtifact/reducer";

import BlueprintSelector from "components/ChatGPT/BlueprintSelector";
import DocumentSelector from "components/LanguageModelArtifact/DocumentSelector";
import DatasetSelector from "components/Dataset/DatasetSelector";
import ShareChatApp from "components/ChatApp/ShareChatApp";
import DynamicWidthSelector from "components/ChatGPT/DynamicWidthSelector";
import SmallDateChart from "components/Tiles/Sense/SmallDateChart";
import circleCheckGreen from "images/Circle Check Light 2.svg";

import styles from "./style.module.css";

import AccountSelector from "containers/Accounts/AccountSelector";
import ArtifactSelector from "components/LanguageModelArtifact/ArtifactSelector";
import { getSelectedAccountId } from "containers/Accounts/reducer";
import { getSelectedFolder } from "containers/Folders/reducer";
import ChatAppWarning from "components/ChatApp/ChatAppWarning";
import { CHATAPP_IMAGE_SIZE } from "containers/ChatApp/reducer";
import { formatBytes } from "containers/Upload/reducer";

import { isEnabled } from "utils/featureFlags";
import { toast } from "react-toastify";
import { push } from "connected-react-router";
import {
  getMemoryAugmentationPromptTemplate,
  getMemoryCreationPromptTemplate,
  getMultiSessionMemoryCount,
  getMultiSessionMemoryCreationEnabled,
  getMultiSessionMemoryEnabled
} from "@/containers/ChatGPT/features/ConversationMemory/selectors";

const ChatAppEdit = ({ editMode } = {}) => {
  const dispatch = useDispatch();

  const chatApp = useSelector((state) => chatGPTReducer.getChatApp(state));

  const nextId = useSelector((state) => nextReducer.getCurrentNextId(state));

  const fewShotExamples = useSelector((state) => nextReducer.getFewShotExamples(state, nextId));

  const hasFewShot = Boolean(fewShotExamples) && Object.keys(fewShotExamples).length > 0;

  const saveFewShot = useSelector((state) => chatGPTReducer.getChatAppSaveFewShot(state));
  const updateSaveFewShot = (val) => dispatch(chatGPTReducer.updateChatAppSaveFewShot(val));

  const [base64StringLogo, setBase64StringLogo] = useState(chatApp?.wizard?.base64StringLogo ?? "");
  const [base64StringBot, setBase64StringBot] = useState(chatApp?.wizard?.base64StringBot ?? "");

  const handleImageUpload = (e, callback, targetImg) => {
    const fileSize = e.target.files[0]?.size;
    if (fileSize > CHATAPP_IMAGE_SIZE) {
      let message = `The image you uploaded was ${formatBytes(fileSize)}. Please upload an image smaller than 500KB.`;
      toast(message, {
        type: toast.TYPE.ERROR,
        position: toast.POSITION.TOP_CENTER,
        autoClose: 7500
      });
      e.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (targetImg === "LOGO") {
        setBase64StringLogo(reader.result);
      } else if (targetImg === "BOT") {
        setBase64StringBot(reader.result);
      }
      callback(reader.result);
    };

    reader.readAsDataURL(e.target.files[0]);
  };

  const handleLogoChange = (e) => {
    const callback = (value) => dispatch(chatGPTReducer.updateBase64StringLogo(value));
    return handleImageUpload(e, callback, "LOGO");
  };

  const handleBotChange = (e) => {
    const callback = (value) => dispatch(chatGPTReducer.updateBase64StringBot(value));
    return handleImageUpload(e, callback, "BOT");
  };

  const [headerColor, setHeaderColor] = useState(chatApp?.wizard?.headerColor ?? "#401570");
  const [editHeaderColor, setEditHeaderColor] = useState(false);
  const headerDarkMode = useSelector((state) => chatGPTReducer.getHeaderDarkMode(state));

  const [linkColor, setLinkColor] = useState(chatApp?.wizard?.linkColor ?? "#8556EB");
  const [editLinkColor, setEditLinkColor] = useState(false);
  const linkColorDarkMode = useSelector((state) => chatGPTReducer.getLinkColorDarkMode(state));

  const [chatButtonColor, setChatButtonColor] = useState(chatApp?.wizard?.chatButtonColor ?? "#FB813C");
  const [editChatButtonColor, setEditChatButtonColor] = useState(false);
  const chatButtonDarkMode = useSelector((state) => chatGPTReducer.getChatButtonDarkMode(state));

  const [backgroundColor, setBackgroundColor] = useState(chatApp?.wizard?.backgroundColor ?? "#FFFFFF");
  const [editBackgroundColor, setEditBackgroundColor] = useState(false);
  const backgroundColorDarkMode = useSelector((state) => chatGPTReducer.getBackgroundColorDarkMode(state));

  const description = useSelector((state) => chatAppReducer.getAppDescription(state));
  const setDescription = (val) => dispatch(chatAppReducer.updateAppDescription(val));

  const name = useSelector((state) => chatAppReducer.getAppName(state)) ?? "";
  const setName = (val) => dispatch(chatAppReducer.updateAppName(val));

  const [nextChat, setNextChat] = useState(chatApp?.wizard?.nextChat ?? "");

  const isCreatingApp = useSelector((state) => chatAppReducer.getIsCreatingApp(state));

  const chatButtonCompany = useSelector((state) => chatAppReducer.getAppCompany(state));
  const updateChatButtonCompany = (val) => dispatch(chatAppReducer.updateAppCompany(val));

  const steps = useSelector((state) => chatAppReducer.getSteps(state));
  const setSteps = (steps) => dispatch(chatAppReducer.updateSteps(steps));

  const [stepIndex, setStepIndex] = useState(0);

  const isAsking = useSelector((state) => chatGPTReducer.getIsAsking(state));

  const isNextSaving = useSelector((state) => nextReducer.getIsSavingReport(state, nextId));

  const disableButtons = isAsking === true || isNextSaving;
  const selectedBlueprintId = useSelector((state) => chatGPTReducer.getBlueprintId(state));
  const blueprints = useSelector((state) => viewReducer.getChatBlueprints(state));
  const bpOptions = blueprints.map((bp) => ({
    ...bp,
    label: bp?.name,
    value: bp?.id
  }));
  const allowedAppUseCases = useSelector((state) => chatAppReducer.getAllowedAppUseCases(state));
  const updateAllowedAppUseCases = (val) => dispatch(chatAppReducer.updateAllowedAppUseCases(val));
  const allowedAppDatasets = useSelector((state) => chatAppReducer.getAllowedAppDatasets(state));
  const updateAllowedAppDatasets = (val) => dispatch(chatAppReducer.updateAllowedAppDatasets(val));

  const documentList = useSelector((state) => languageModelArtifactReducer.getCorpusList(state));
  const artifactId = useSelector((state) => chatGPTReducer.getArtifactId(state));
  const allowedAppDocuments = useSelector((state) => chatAppReducer.getAllowedAppDocuments(state));
  const updateAllowedAppDocuments = (val) => dispatch(chatAppReducer.updateAllowedAppDocuments(val));
  const updateArtifactId = (val) => dispatch(chatGPTReducer.updateArtifactId(val));
  const datasetId = useSelector((state) => nextReducer.getDatasetId(state, "new"));
  const chatType = useSelector((state) => chatGPTReducer.getChatType(state));
  const updateDatasetId = (val) => dispatch(nextReducer.updateDatasetId("new", val));
  const selectedFolder = useSelector((state) => foldersReducer.getSelectedFolder(state));
  const userEditPermission = useSelector((state) => foldersReducer.getUserEditPermission(state, selectedFolder));

  const datasets = useSelector((state) => viewReducer.getDatasets(state));
  const dataset = useSelector((state) => homeReducer.getDataset(state, datasetId));
  const updateComparisonOptions = (val) => dispatch(nextReducer.updateComparisonOptions("new", val));
  const changeTimestampColumn = (datasetId, timestampCol) =>
    dispatch(homeReducer.changeTimestampColumn(datasetId, timestampCol));
  const comparisonOptions = useSelector((state) => nextReducer.getComparisonOptions(state, "new"));

  const dateFieldOptions =
    dataset?.ranked_fields !== undefined
      ? dataset.ranked_fields
          .filter((f) => f.type === "date")
          .map((f) => ({
            value: f,
            label: f.label
          }))
      : [];

  const documentOptions = documentList?.map((d) => {
    return {
      label: d?.name,
      value: d?.id
    };
  });

  let showDatasets = chatGPTReducer.getIsDatasetChat(undefined, chatType);
  let showDocuments = chatGPTReducer.getIsDocumentChat(undefined, chatType);
  let showAllDatasets = chatType === "SQL";
  let onlyWhatsChanged = chatType === "WHATS_CHANGED";
  let bpIsAnalytics = chatType === "AIBLE";
  blueprints?.forEach((bp) => {
    if (allowedAppUseCases.includes(bp?.id)) {
      if (chatGPTReducer.getIsDocumentChat(undefined, bp?.wizard?.chatGPT?.chatType)) {
        showDocuments = true;
      }
      if (bp?.wizard?.chatGPT?.chatType === "SQL") {
        showAllDatasets = true;
      }
      if (chatGPTReducer.getIsDatasetChat(undefined, bp?.wizard?.chatGPT?.chatType)) {
        showDatasets = true;
      }
      if (bp?.wizard?.chatGPT?.chatType === "SQL" || bp?.wizard?.chatGPT?.chatType === "AIBLE") {
        onlyWhatsChanged = false;
      }
      if (bp?.wizard?.chatGPT?.chatType === "AIBLE") {
        bpIsAnalytics = true;
      }
    }
  });

  const datasetOptions = datasets
    ?.filter((d) => {
      // if (!(d?.outcome !== undefined && d?.rowCount > 0)) {
      //   return false;
      // }
      if (d?.dataset_status === "ERROR") {
        return false;
      }
      if (onlyWhatsChanged) {
        const hasFilteredField = d?.ranked_fields?.find((f) => f.type === "date");
        if (!hasFilteredField) {
          return false;
        }
      }
      return true;
    })
    ?.map((d) => ({
      label: `(${d?.id}) ${d?.label}`,
      value: d?.id
    }));

  const dateField = comparisonOptions?.dateField
    ? dateFieldOptions.find((o) => o?.value === comparisonOptions?.dateField)
    : undefined;
  const datasetDateField = dataset?.options?.timestamp_column;

  const chatAppStep = useSelector((state) => chatGPTReducer.getChatAppStep(state));

  const [WHATS_CHANGEDReanalysis, updateWHATS_CHANGEDReanalysis] = useState(false);

  const startInDashboard = useSelector((state) => chatAppReducer.getStartInDashboard(state));
  const updateStartInDashboard = (val) => dispatch(chatAppReducer.updateStartInDashboard(val));

  const addTrainingDataset = useSelector((state) => chatAppReducer.getAddTrainingDataset(state));
  const updateAddTrainingDataset = (bool) => dispatch(chatAppReducer.updateAddTrainingDataset(bool));
  const currentArtifact = useSelector((state) => nextReducer.getCurrentArtifact(state, nextId));
  const updateCurrentArtifact = (artifact) => dispatch(nextReducer.updateCurrentArtifact(nextId, artifact));

  const chatRestrictChartsToPinned = useSelector((state) => chatGPTReducer.getChatRestrictChartsToPinned(state));
  const updateChatRestrictChartsToPinned = (val) => dispatch(chatGPTReducer.updateChatRestrictChartsToPinned(val));

  const [artifactSelect, updateArtifactSelect] = useState(currentArtifact);
  const [createTrainingDataName, updateCreateTrainingDataName] = useState("New FT Dataset");

  const accountId = useSelector((state) => getSelectedAccountId(state));
  const folderId = useSelector((state) => getSelectedFolder(state));

  const selectedBlueprint = blueprints.find((bp) => bp?.id === selectedBlueprintId);
  const hasAutomaticQuestions = selectedBlueprint?.wizard?.chatGPT?.replayPrompts?.length > 0;
  const modelId = useSelector((state) => chatGPTReducer.getModelId(state));
  const secondaryModelId = useSelector((state) => chatGPTReducer.getSecondaryModel(state));
  const shouldShowAppWarning = hasAutomaticQuestions && chatGPTReducer.getIsGoogleModel(modelId);

  const [createdChatAgentId, setCreatedChatAgentId] = useState(undefined);
  const [showShareModal, setShowShareModal] = useState(false);

  const hideOutcomeSelection = useSelector((state) => chatAppReducer.getHideOutcomeSelection(state));

  const addToAzTest = useSelector((state) => chatAppReducer.getAddToAzTest(state));
  const updateAddToAzTest = (val) => dispatch(chatAppReducer.updateAddToAzTest(val));
  const azTestApps = useSelector((state) => chatAppReducer.getActiveAZTests(state));
  const azTestOptions = azTestApps.map((app) => {
    return {
      value: app?.id,
      label: app?.name
    };
  });

  const NO_TEST_OPTION = {
    value: undefined,
    label: "No Test"
  };

  azTestOptions.unshift(NO_TEST_OPTION);

  const selectedAddToTest = azTestOptions?.find((o) => o?.value === addToAzTest);

  const chatApps = useSelector((state) => viewReducer.getChatApps(state));
  const appAZAllocation = useSelector((state) => chatAppReducer.getAppAZAllocation(state));
  const updateAppAZAllocation = (val) => dispatch(chatAppReducer.updateAppAZAllocation(val));
  const appAZDistribution = useSelector((state) => chatAppReducer.getAppAZDistribution(state));
  const updateAppAZDistribution = (obj) => dispatch(chatAppReducer.updateAppAZDistribution(obj));

  const targetAzTest = useSelector((state) => chatAppReducer.getCurrentAZTest(state, addToAzTest));
  const savedDistribution = {
    ...targetAzTest?.appDistribution
  };

  const currentDistribution = Object.keys(appAZDistribution)?.reduce((acc, v) => {
    const currVal = appAZDistribution[v] ?? 0;
    return acc + currVal;
  }, 0);

  const totalDistribution = currentDistribution + appAZAllocation;

  const hideChangeInBehavior = useSelector((state) => chatGPTReducer.getHideChangeInBehavior(state));
  const hideChangeInPopulation = useSelector((state) => chatGPTReducer.getHideChangeInPopulation(state));
  const hideChangeInOutcome = useSelector((state) => chatGPTReducer.getHideChangeInOutcome(state));
  const hideChangeInTotalPopulation = useSelector((state) => chatGPTReducer.getHideChangeInTotalPopulation(state));
  const renameBehavior = useSelector((state) => chatGPTReducer.getRenameBehavior(state));
  const renameOutcome = useSelector((state) => chatGPTReducer.getRenameOutcome(state));
  const renamePopulation = useSelector((state) => chatGPTReducer.getRenamePopulation(state));
  const renameTotalPopulation = useSelector((state) => chatGPTReducer.getRenameTotalPopulation(state));
  const hideMissingInCharts = useSelector((state) => chatGPTReducer.getHideMissingInCharts(state));
  const hideOtherInCharts = useSelector((state) => chatGPTReducer.getHideOtherInCharts(state));
  const chatFlow = useSelector((state) => chatGPTReducer.getChatFlow(state));
  const multiSessionMemoryEnabled = useSelector((state) => getMultiSessionMemoryEnabled(state));
  const multiSessionMemoryCount = useSelector((state) => getMultiSessionMemoryCount(state));
  const multiSessionMemoryCreationEnabled = useSelector((state) => getMultiSessionMemoryCreationEnabled(state));
  const memoryAugmentationPromptTemplate = useSelector((state) => getMemoryAugmentationPromptTemplate(state));
  const memoryCreationPromptTemplate = useSelector((state) => getMemoryCreationPromptTemplate(state));
  const comparisonChartOptions = useSelector((state) => nextReducer.getComparisonChartOptions(state, nextId));

  const chatAppState = {
    //  Original chatAppState stuff
    base64StringLogo,
    base64StringBot,
    chatButtonCompany,
    headerColor,
    linkColor,
    chatButtonColor,
    backgroundColor,
    name,
    description,
    nextChat,
    headerDarkMode,
    linkColorDarkMode,
    chatButtonDarkMode,
    backgroundColorDarkMode,
    // Updated chatAppStuff that exists on agent template creation
    // Taking state from when creating agent from a project or from the loaded chatApp.
    templateType: chatType,
    hideChangeInBehavior: hideChangeInBehavior,
    hideChangeInPopulation: hideChangeInPopulation,
    hideChangeInOutcome: hideChangeInOutcome,
    hideChangeInTotalPopulation: hideChangeInTotalPopulation,
    renameBehavior: renameBehavior,
    renameOutcome: renameOutcome,
    renamePopulation: renamePopulation,
    renameTotalPopulation: renameTotalPopulation,
    hideMissingInCharts: hideMissingInCharts,
    hideOtherInCharts: hideOtherInCharts,
    hideDatesOutsideRange: chatApp?.wizard?.hideDatesOutsideRange,
    autoDeepDive: chatApp?.wizard?.autoDeepDive,
    autoDeepDiveColumns: chatApp?.wizard?.autoDeepDiveColumns,
    autoDeepDiveUseCustomLabels: chatApp?.wizard?.autoDeepDiveUseCustomLabels,
    autoDeepDiveCustomLabels: chatApp?.wizard?.autoDeepDiveCustomLabels,
    optimizeSetup: chatApp?.wizard?.optimizeSetup,
    multiSessionMemoryEnabled: multiSessionMemoryEnabled,
    multiSessionMemoryCreationEnabled: multiSessionMemoryCreationEnabled,
    multiSessionMemoryCount: multiSessionMemoryCount,
    memoryAugmentationPromptTemplate: memoryAugmentationPromptTemplate,
    memoryCreationPromptTemplate: memoryCreationPromptTemplate,
    linkedOptimizeReport: chatApp?.wizard?.linkedOptimizeReport,
    tags: [...(chatApp?.wizard?.tags ?? [])],
    chatFlow,
    comparisonChartOptions: comparisonChartOptions
  };

  useEffect(() => {
    updateAppAZDistribution(savedDistribution);
  }, [addToAzTest]);

  const renderStep = (step, i) => {
    const shouldShowDocuments =
      step?.chatBlueprintId === "DOCUMENT" ||
      step?.chatBlueprintId === "CHATAIBLE_DOCUMENT_SUMMARIZATION" ||
      showDocuments;
    const shouldShowDatasets = step?.chatBlueprintId === "CHATAIBLE_ANALYTICS" || showDatasets;
    const isAnalyticsChat = step?.chatBlueprintId === "CHATAIBLE_ANALYTICS" || bpIsAnalytics;

    const isAnalyticsOrWhatsChanged =
      step?.chatBlueprintId === "CHATAIBLE_ANALYTICS" ||
      bpIsAnalytics ||
      step?.chatBlueprintId === "WHATS_CHANGED" ||
      onlyWhatsChanged;

    const appInitSummarize = useSelector((state) => chatGPTReducer.getAppInitSummarize(state));
    const updateAppInitSummarize = (val) => dispatch(chatGPTReducer.updateAppInitSummarize(val));

    const replayPrompts = useSelector((state) => chatGPTReducer.getReplayPrompts(state));
    const appInitAutoAsk = useSelector((state) => chatGPTReducer.getAppInitAutoAsk(state));
    const updateAppInitAutoAsk = (val) => dispatch(chatGPTReducer.updateAppInitAutoAsk(val));

    return (
      <div>
        <div className={styles.stepTitle}>{"Chat Flow Settings"}</div>
        <div>{"Agent Template: "}</div>
        <BlueprintSelector
          hideSave
          menuPlacement={"bottom"}
          isDynamic
          isStyled
          style={{
            marginLeft: "-5px",
            marginBottom: "10px"
          }}
          // extraOptions={[{ label: "Custom", value: "CUSTOM_PROMPT" }]}
          // }
          // value={
          //   step?.chatBlueprintId !== undefined
          //     ? step?.chatBlueprintId
          //     : "CUSTOM_PROMPT"
          // }
        />
        {userEditPermission === true && (
          <div className={styles.cSetting} style={{ marginBottom: "20px" }}>
            <div style={{ marginBottom: "2px" }}>{"Additional Agent Templates (Optional)"}</div>
            <Select
              options={bpOptions.filter(
                (option) => !allowedAppUseCases.includes(option?.value) && option?.id !== selectedBlueprintId
              )}
              onChange={(selectedOptions) => {
                const selectedId = selectedOptions.map((option) => option?.value);
                const retIds = [...selectedId, selectedBlueprintId];
                updateAllowedAppUseCases(retIds);
              }}
              menuPlacement={"bottom"}
              value={bpOptions.filter(
                (option) => allowedAppUseCases.includes(option?.value) && option?.id !== selectedBlueprintId
              )}
              isMulti
              placeholder={"Select All Additional Agent Templates For This Agent"}
              isDisabled={isAsking}
            />
          </div>
        )}
        {shouldShowDocuments && (
          <React.Fragment>
            <div className={styles.chatSelectorsCopy}>{"I will reference "}</div>
            <DocumentSelector
              artifactId={step?.artifactId}
              onChange={(val) => {
                updateArtifactId(val?.id);
                setSteps({ ...steps, [i]: { ...step, artifactId: val?.id } });
              }}
              hideUpload
              isDynamic
              isStyled
              style={{
                marginLeft: "-5px"
              }}
              forceSelection
              menuPlacement={"bottom"}
            />
            <div className={styles.chatSelectorsCopy} style={{ marginBottom: "20px" }}>
              {" to answer your summarization questions."}
            </div>
            {userEditPermission === true && Boolean(step?.artifactId) && (
              <div className={styles.cSetting} style={{ marginBottom: "20px" }}>
                <div style={{ marginBottom: "2px" }}>{"Additional Documents (Optional)"}</div>
                <Select
                  options={documentOptions.filter(
                    (option) => !allowedAppDocuments.includes(option?.value) && option?.value !== artifactId
                  )}
                  onChange={(selectedOptions) => {
                    const selectedId = selectedOptions.map((option) => option?.value);
                    const retIds = [...selectedId, artifactId].filter((d) => d);
                    updateAllowedAppDocuments(retIds);
                  }}
                  menuPlacement={"bottom"}
                  value={documentOptions.filter(
                    (option) => allowedAppDocuments.includes(option?.value) && option?.value !== artifactId
                  )}
                  isMulti
                  placeholder={"Select Additional Documents For This Agent"}
                  isDisabled={isAsking}
                />
              </div>
            )}
          </React.Fragment>
        )}
        {shouldShowDatasets && (
          <React.Fragment>
            <div className={styles.chatSelectorsCopy}>{"I will scan "}</div>
            <DatasetSelector
              datasetId={datasetId}
              onChange={(val) => {
                updateDatasetId(val);
              }}
              isDynamic
              isStyled
              style={{
                marginLeft: "-5px"
              }}
              menuPlacement={"bottom"}
              forceDatasetSelection
              allowAllDatasets={
                chatType === "SQL" || (!chatGPTReducer.getIsDatasetChat(undefined, chatType) && showAllDatasets)
              }
              filter={chatType === "WHATS_CHANGED" ? "date" : undefined}
            />
            {chatType === "WHATS_CHANGED" ? (
              <React.Fragment>
                <div className={styles.chatSelectorsCopy}>
                  {` to answer your questions about what's changed since `}
                </div>
                <DynamicWidthSelector
                  options={dateFieldOptions}
                  onChange={(v) => {
                    if (v?.value !== comparisonOptions?.dateField) {
                      updateComparisonOptions({
                        ...comparisonOptions,
                        dateField: v?.label
                      });
                    }
                  }}
                  isDynamic
                  isDisabled={!dataset}
                  menuPlacement={"top"}
                  value={
                    comparisonOptions?.dateField
                      ? {
                          label: comparisonOptions.dateField,
                          value: comparisonOptions.dateField
                        }
                      : undefined
                  }
                  placeholder={!dataset ? "No Dataset Selected" : "Select a Date Field"}
                  isStyled
                  style={{
                    marginLeft: "-5px"
                  }}
                />
                {dataset && comparisonOptions?.dateField !== undefined ? (
                  comparisonOptions?.dateField !== datasetDateField ? (
                    <React.Fragment>
                      <div className={styles.chatSelectorsCopy}>
                        {"This dataset will need to be reanalyzed to see what's changed."}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          width: "100%",
                          justifyContent: "center",
                          alignItems: "center",
                          marginTop: "8px"
                        }}
                      >
                        <Button
                          type={"primary"}
                          onClick={() => {
                            updateWHATS_CHANGEDReanalysis(true);
                            return changeTimestampColumn(dataset.id, comparisonOptions?.dateField)
                              .then(() => {
                                updateWHATS_CHANGEDReanalysis(false);
                              })
                              .catch((e) => {
                                console.log(e);
                                updateWHATS_CHANGEDReanalysis(false);
                              });
                          }}
                        >
                          {"Analyze " + comparisonOptions?.dateField}
                        </Button>
                      </div>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <div className={styles.chatSelectorsCopy}>{" was between "}</div>
                      <SmallDateChart
                        datasetId={dataset?.id}
                        updateComparisonOptions={updateComparisonOptions}
                        comparisonOptions={comparisonOptions}
                        field={dataset?.fields?.[comparisonOptions?.dateField]}
                        hideChart
                        hideText
                        style={{
                          width: "50%",
                          minWidth: "300px",
                          marginTop: "0px",
                          marginLeft: "10px",
                          fontSize: "16px"
                        }}
                      />
                    </React.Fragment>
                  )
                ) : (
                  <React.Fragment />
                )}
              </React.Fragment>
            ) : (
              <div className={styles.chatSelectorsCopy} style={{ marginBottom: "20px" }}>
                {` to answer your ${chatType === "SQL" ? "NLQ" : "analytics"} questions.`}
              </div>
            )}
            {userEditPermission === true && !isNaN(datasetId) && (
              <div className={styles.cSetting} style={{ marginBottom: "20px" }}>
                <div style={{ marginBottom: "2px" }}>{"Additional Datasets (Optional)"}</div>
                <Select
                  options={datasetOptions.filter(
                    (option) => !allowedAppDatasets.includes(option?.value) && option?.value !== datasetId
                  )}
                  onChange={(selectedOptions) => {
                    const selectedId = selectedOptions.map((option) => option?.value);
                    const retIds = [...selectedId, datasetId].filter((d) => d);
                    updateAllowedAppDatasets(retIds);
                  }}
                  menuPlacement={"bottom"}
                  value={datasetOptions.filter(
                    (option) => allowedAppDatasets.includes(option?.value) && option?.value !== datasetId
                  )}
                  isMulti
                  allowAllDatasets={showAllDatasets}
                  placeholder={"Select Additional Datasets For This Agent"}
                  isDisabled={isAsking}
                />
              </div>
            )}
            <div className={styles.settingFlex}>
              <div className={styles.settingLabel}>
                {"Allow users to change dataset KPI and edit Dataset Column Details"}
                <Tooltip
                  overlay={
                    "Enable the KPI selection flow for chat agent users and let chat app users edit the Dataset Column Details"
                  }
                />
              </div>
              <LabeledToggle
                style={{ display: "block" }}
                checked={!hideOutcomeSelection}
                onChange={() => {
                  dispatch(chatAppReducer.updateHideOutcomeSelection(!hideOutcomeSelection));
                }}
                disabled={isAsking}
                isAibleColors
              />
            </div>
          </React.Fragment>
        )}
        <div className={styles.settingFlex}>
          <div className={styles.settingLabel}>
            {"Start chat from dashboard view"}
            <Tooltip overlay={"Start the chat app in the dashboard view"} />
          </div>
          <LabeledToggle
            style={{ display: "block" }}
            checked={startInDashboard === true}
            onChange={() => {
              if (startInDashboard) {
                updateStartInDashboard(false);
              } else {
                updateStartInDashboard(true);
              }
            }}
            disabled={isAsking}
            isAibleColors
          />
        </div>
        {isEnabled("model-fine-tuning") && (
          <div className={styles.settingFlex}>
            <div className={styles.settingLabel}>
              {"Collect agent user feedback for AI post training"}
              <Tooltip overlay={"Select a training dataset to collect feedback from users"} />
            </div>
            <LabeledToggle
              style={{ display: "block" }}
              checked={addTrainingDataset}
              onChange={() => {
                if (addTrainingDataset) {
                  updateAddTrainingDataset(false);
                } else {
                  updateAddTrainingDataset(true);
                }
              }}
              disabled={isAsking}
              isAibleColors
            />
          </div>
        )}
        {isEnabled("model-fine-tuning") && addTrainingDataset && (
          <div className={styles.trainingDatasetSelectWrapper}>
            {/* <div className={styles.feedbackBodyText}>
              {"Any LLM response that you like can be saved as fine-tuning data for your organization. Once enough feedback has been provided your organization will be able to create an updated model that behaves more like the responses from your feedback."}
            </div>
            <div
              className={styles.feedbackBodyText}
              style={{marginBottom: "20px"}}
            >
              {"This data is only available to users in your organization."}
            </div> */}
            <ArtifactSelector
              type="FINE_TUNING_DATA"
              artifactId={currentArtifact?.id}
              onChange={(a) => {
                updateArtifactSelect(a);
                dispatch(nextReducer.updateCurrentArtifact(nextId, a));

                if (a !== "CREATE_NEW") {
                  updateCreateTrainingDataName(a?.name);
                }

                // setDataId(a.id);
              }}
              labelOverride={"Where do you want to save user feedback?"}
            />
            {artifactSelect?.name === "Create New" && (
              <div className={styles.createNewTrainingData}>
                <div className={styles.trainingModalSubtitle}>{"New Training Dataset Name"}</div>
                <LineInput
                  isBox
                  placeholder={"Add a name for the new fine tuned model"}
                  value={createTrainingDataName}
                  inputClassName={styles.trainingModalInput}
                  onChange={(val) => updateCreateTrainingDataName(val)}
                  updateInstant={true}
                />
                <AccountSelector />
                <div />
                <Button
                  style={{ marginTop: "5px" }}
                  onClick={() =>
                    dispatch(createArtifact(accountId, folderId, "FINE_TUNING_DATA", createTrainingDataName)).then(
                      (artifact) => {
                        updateArtifactSelect(artifact);
                        // setDataId(artifact.id);
                        console.log("created artifact", artifact);
                        console.log("Updating current artifact", artifact.id);
                        updateCurrentArtifact(artifact);
                      }
                    )
                  }
                >
                  {"Create New Training Dataset"}
                </Button>
              </div>
            )}
          </div>
        )}
        {isEnabled("az-testing") && (
          <div className={styles.settingFlex}>
            <div className={styles.settingLabel}>{"Include agent in an A to Z test"}</div>
            <div className={styles.testSelectorWrapper}>
              <Select
                options={azTestOptions}
                value={selectedAddToTest}
                onChange={(val) => updateAddToAzTest(val.value)}
              />
            </div>
          </div>
        )}
        {isEnabled("az-testing") && addToAzTest && (
          <div className={styles.appDistributionWrapper}>
            <div className={styles.modalItemLabels}>
              <div className={styles.testModalLabel}>{"Agents in test"}</div>
              <div className={styles.testModalLabel}>{"Distribution"}</div>
            </div>
            {Object.keys(appAZDistribution)?.map((appId, index) => {
              const app = chatApps?.find((i) => i?.id == appId);
              return (
                <div className={styles.appItem} key={"distr_" + appId}>
                  <div className={styles.appName}>{app?.name}</div>
                  <div className={styles.distributionInput}>
                    <LineInput
                      style={{ width: "80px" }}
                      value={appAZDistribution[appId] ?? 0}
                      onChange={(val) => {
                        const retDistribution = {
                          ...appAZDistribution,
                          [appId]: Number(val)
                        };
                        updateAppAZDistribution(retDistribution);
                      }}
                    />
                    <span style={{ marginLeft: "4px" }}>{"%"}</span>
                  </div>
                </div>
              );
            })}
            <div className={styles.appItem}>
              <div className={styles.appName}>{name?.length > 0 ? name : "Unnamed Agent"}</div>
              <div className={styles.distributionInput}>
                <LineInput
                  style={{ width: "80px" }}
                  value={appAZAllocation ?? 0}
                  onChange={(val) => {
                    updateAppAZAllocation(Number(val) || 0);
                  }}
                />
                <span style={{ marginLeft: "4px" }}>{"%"}</span>
              </div>
            </div>
            {totalDistribution > 100 ? (
              <div className={styles.modalDistributionWarning}>
                {`${Math.abs(
                  totalDistribution - 100
                )}% over allocated. To create this app your allocation must be equal to 100% when adding to an A to Z test.`}
              </div>
            ) : totalDistribution < 100 ? (
              <div className={styles.modalDistributionWarning} style={{ color: "#7c7c7c" }}>
                {`${
                  totalDistribution - 100
                }% unallocated. To create this app your allocation must be equal to 100% when adding to an A to Z test.`}
              </div>
            ) : null}
          </div>
        )}
        {isAnalyticsChat && (
          <div className={styles.settingFlex}>
            <div className={styles.settingLabel}>{"Restrict to pinned explore charts"}</div>
            <LabeledToggle
              style={{ display: "block" }}
              checked={chatRestrictChartsToPinned}
              onChange={() => {
                if (chatRestrictChartsToPinned) {
                  return updateChatRestrictChartsToPinned(false);
                }
                return updateChatRestrictChartsToPinned(true);
              }}
              disabled={disableButtons}
              isAibleColors
            />
          </div>
        )}
        {isAnalyticsOrWhatsChanged && (
          <div className={styles.settingFlex}>
            <div className={styles.settingLabel}>{"Start Agent with Automatic Summary"}</div>
            <LabeledToggle
              style={{ display: "block" }}
              checked={appInitSummarize}
              onChange={() => {
                return updateAppInitSummarize(!appInitSummarize);
              }}
              disabled={disableButtons}
              isAibleColors
            />
          </div>
        )}
        {replayPrompts?.length > 0 && (
          <div className={styles.settingFlex}>
            <div className={styles.settingLabel}>{"Automatically Ask Saved Questions"}</div>
            <LabeledToggle
              style={{ display: "block" }}
              checked={appInitAutoAsk}
              onChange={() => {
                return updateAppInitAutoAsk(!appInitAutoAsk);
              }}
              disabled={disableButtons}
              isAibleColors
            />
          </div>
        )}
        {isEnabled("better-few-shot") && hasFewShot && (
          <div className={styles.settingFlex}>
            <div className={styles.settingLabel}>{"Apply saved Few Shot examples from this project"}</div>
            <LabeledToggle
              style={{ display: "block" }}
              checked={saveFewShot}
              onChange={() => {
                updateSaveFewShot(!saveFewShot);
              }}
              disabled={disableButtons}
              isAibleColors
            />
          </div>
        )}
        <div className={styles.cSetting}>
          <div className={styles.settingLabel} style={{ marginTop: "8px" }}>
            {(i === 0 ? "Initial" : "Next") + " Chat Message"}
            <Tooltip overlay={"This is the first message that the user will see from the Chat Agent Bot"} />
          </div>
          <textarea
            className={styles.settingPrompt}
            value={step?.initialChatText}
            onChange={(val) =>
              setSteps({
                ...steps,
                [i]: { ...step, initialChatText: val.target.value }
              })
            }
          />
        </div>
        {/* {step?.chatBlueprintId === "CUSTOM_PROMPT" && (
          <div className={styles.cSetting}>
            <div className={styles.settingLabel} style={{ marginTop: "8px" }}>
              {"Custom Prompt Augmentation"}
              <Tooltip
                overlay={
                  "This will get pasted before the user's prompt to the LLM"
                }
              />
            </div>
            <textarea
              className={styles.settingPrompt}
              value={step?.promptAug}
              onChange={(val) =>
                setSteps({
                  ...steps,
                  [i]: { ...step, promptAug: val.target.value },
                })
              }
            />
          </div>
        )} */}
        <div className={styles.cSetting}>
          <div className={styles.settingLabel} style={{ marginTop: "8px" }}>
            {"Placeholder"}
            <Tooltip overlay={"This will set the placeholder text for the user's input"} />
          </div>
          <textarea
            className={styles.settingPrompt}
            value={step?.placeholderText ?? "Start Chatting"}
            onChange={(val) =>
              setSteps({
                ...steps,
                [i]: { ...step, placeholderText: val.target.value }
              })
            }
          />
        </div>

        {/* <div className={styles.cSetting}>
          <div className={styles.settingLabel} style={{ marginTop: "8px" }}>
            {"Use Last Response"}
            <Tooltip
              overlay={
                "This will append the last response from the Chat Bot into the new prompt"
              }
            />
          </div>
          <input
            type="checkbox"
            checked={step?.usePrevResponse === true}
            onChange={(val) =>
              setSteps({
                ...steps,
                [i]: { ...step, usePrevResponse: !step?.usePrevResponse },
              })
            }
            disabled={i === 0}
          />
        </div>
        <Button
          onClick={() => {
            if (
              confirm("Are you sure? This will delete this step (" + i + ")")
            ) {
              setSteps({ ...steps, [i]: { ...step, isDeleted: true } });
            }
          }}
          style={{ marginBottom: "20px" }}
          disabled={i === 0}
        >
          {"Delete Step"}
        </Button> */}
      </div>
    );
  };

  const closeAllColorPickers = () => {
    setEditHeaderColor(false);
    setEditLinkColor(false);
    setEditChatButtonColor(false);
    setEditBackgroundColor(false);
  };

  return (
    <>
      {shouldShowAppWarning && <ChatAppWarning />}
      {createdChatAgentId && (
        <Alert bsStyle="success">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}
            >
              <img src={circleCheckGreen} style={{ width: "20px" }} />
              <strong>Your Agent was successfully created!</strong>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <Button
                onClick={() => {
                  dispatch(chatGPTReducer.resetGPT());
                  dispatch(push("/next/new/new/" + createdChatAgentId));
                  dispatch(chatGPTReducer.initChatAppHelper(createdChatAgentId));
                }}
              >
                Open
              </Button>
              <ShareChatApp
                id={createdChatAgentId}
                showModal={showShareModal}
                updateShowModal={() => setShowShareModal(true)}
              >
                Share Public URL
              </ShareChatApp>
            </div>
          </div>
        </Alert>
      )}
      <div>
        <div className={styles.cSetting}>
          <div className={styles.subTitle}>
            {"Name"}
            {!(name?.length > 0) && <span className={styles.errorText}>{"Please enter a name for this agent"}</span>}
          </div>
          <div style={{ display: "flex", width: "100%", gap: "10px" }}>
            <LineInput value={name} onChange={setName} updateInstant={true} style={{ flexGrow: 1 }} />
            <div className={styles.actionsButtonWrapper} style={{ flexGrow: "0" }}>
              <Button
                className={styles.saveChatApp}
                onClick={async () => {
                  if (chatRestrictChartsToPinned || saveFewShot) {
                    return dispatch(nextReducer.saveNext(nextId, "", false)).then(async (response) => {
                      console.log("Saving agent after first saving next: ", response);
                      const savedApp = await dispatch(
                        chatAppReducer.saveChatApp(
                          chatAppState,
                          undefined,
                          response,
                          false,
                          false,
                          true,
                          false,
                          true,
                          true
                        )
                      );

                      setCreatedChatAgentId(savedApp.id);
                    });
                  }
                  const savedApp = await dispatch(
                    chatAppReducer.saveChatApp(chatAppState, undefined, undefined, true, false, true, false, true, true)
                  );
                  setCreatedChatAgentId(savedApp.id);
                }}
                isLoading={isCreatingApp === true}
                disabled={
                  !(name?.length > 0) ||
                  (addTrainingDataset && !currentArtifact?.id) ||
                  (addToAzTest && totalDistribution !== 100)
                }
                isSmall
                type="secondary"
              >
                {"Create Chat Agent"}
              </Button>
              {chatApp?.id ? (
                <ShareChatApp
                  id={chatApp?.id}
                  style={{
                    backgroundColor: "none",
                    borderRadius: "3px",
                    padding: "5px 12px",
                    marginLeft: "10px"
                  }}
                >
                  {"Add Users"}
                </ShareChatApp>
              ) : null}
            </div>
          </div>
        </div>
        <div className={styles.cSetting}>
          <div className={styles.subTitle}>{"Description"}</div>
          <LineInput value={description} onChange={setDescription} />
        </div>
        <div className={styles.cSetting}>
          <div className={styles.subTitle}>{"Company Name"}</div>
          <LineInput value={chatButtonCompany} onChange={updateChatButtonCompany} />
        </div>
        <div className={styles.cSetting}>
          <div className={styles.subTitle}>{"Logo"}</div>
          {base64StringLogo !== "" && <img src={base64StringLogo} className={styles.viewImage} />}
          <input type="file" accept=".png" onChange={handleLogoChange} />
        </div>
        <div className={styles.cSetting}>
          <div className={styles.subTitle}>{"ChatBot Image"}</div>
          {base64StringBot !== "" && <img src={base64StringBot} className={styles.viewImage} />}
          <input type="file" accept=".png" onChange={handleBotChange} />
        </div>
        <div style={{ marginBottom: "20px" }}>
          <div className={styles.subTitle} style={{ fontSize: "16px" }}>
            {"Brand Colors"}
          </div>
          <div className={styles.colorSettings}>
            <div className={styles.colorSetting}>
              <div
                className={styles.colorTop}
                onClick={() => {
                  closeAllColorPickers();
                  setEditHeaderColor(!editHeaderColor);
                }}
              >
                <div className={styles.colorSubTitle}>{"Header Color"}</div>
                <div className={styles.showColor} style={{ backgroundColor: headerColor }} />
                <LineInput
                  value={headerColor}
                  onChange={(val) => {
                    setHeaderColor(val);
                    dispatch(chatGPTReducer.updateHeaderColor(val));
                  }}
                  updateInstant={true}
                  style={{
                    marginLeft: "12px"
                  }}
                  inputClassName={styles.colorPickerInput}
                />
              </div>
              <div className={styles.darkModeToggleWrapper}>
                <LabeledToggle
                  style={{
                    display: "block"
                  }}
                  checked={headerDarkMode}
                  onChange={() => {
                    if (headerDarkMode !== true) {
                      dispatch(chatGPTReducer.updateHeaderDarkMode(true));
                    } else {
                      dispatch(chatGPTReducer.updateHeaderDarkMode(false));
                    }
                  }}
                  isAibleColors
                />
                <div className={styles.darkModeToggleLabel}>{"Reverse Content Color"}</div>
              </div>
              {editHeaderColor && (
                <div className={styles.colorPickerWrapper + " colorPickerWrapper"}>
                  <div className={styles.colorPickerLabel}>
                    {"Custom Color"}
                    <a className={styles.colorPickerClose} onClick={() => closeAllColorPickers()}>
                      {"✕"}
                    </a>
                  </div>
                  <HexColorPicker
                    color={headerColor}
                    onChange={(val) => {
                      setHeaderColor(val);
                      dispatch(chatGPTReducer.updateHeaderColor(val));
                    }}
                  />
                </div>
              )}
            </div>

            {/* Hiding this until we have cycles to make this better
            <div className={styles.colorSetting}>
              <div
                className={styles.colorTop}
                onClick={() => {
                  closeAllColorPickers();
                  setEditLinkColor(!editLinkColor);
                }}
              >
                <div className={styles.colorSubTitle}>{"Link, Accent and Chatbot Color"}</div>
                <div className={styles.showColor} style={{ backgroundColor: linkColor }} />
                <LineInput
                  value={linkColor}
                  onChange={(val) => {
                    setLinkColor(val);
                    dispatch(chatGPTReducer.updateLinkColor(val));
                  }}
                  updateInstant={true}
                  style={{
                    marginLeft: "12px"
                  }}
                  inputClassName={styles.colorPickerInput}
                />
              </div>
              <div className={styles.darkModeToggleWrapper}>
                <LabeledToggle
                  style={{
                    display: "block"
                  }}
                  checked={linkColorDarkMode}
                  onChange={() => {
                    if (linkColorDarkMode !== true) {
                      dispatch(chatGPTReducer.updateLinkColorDarkMode(true));
                    } else {
                      dispatch(chatGPTReducer.updateLinkColorDarkMode(false));
                    }
                  }}
                  isAibleColors
                />
                <div className={styles.darkModeToggleLabel}>{"Reverse Content Color"}</div>
              </div>
              {editLinkColor && (
                <div className={styles.colorPickerWrapper + " colorPickerWrapper"}>
                  <div className={styles.colorPickerLabel}>
                    {"Custom Color"}
                    <a className={styles.colorPickerClose} onClick={() => closeAllColorPickers()}>
                      {"✕"}
                    </a>
                  </div>
                  <HexColorPicker
                    color={linkColor}
                    onChange={(val) => {
                      setLinkColor(val);
                      dispatch(chatGPTReducer.updateLinkColor(val));
                    }}
                  />
                </div>
              )}
            </div> */}

            <div className={styles.colorSetting}>
              <div
                className={styles.colorTop}
                onClick={() => {
                  closeAllColorPickers();
                  setEditChatButtonColor(!editChatButtonColor);
                }}
              >
                <div className={styles.colorSubTitle}>{"Chat Button Color"}</div>
                <div className={styles.showColor} style={{ backgroundColor: chatButtonColor }} />
                <LineInput
                  value={chatButtonColor}
                  onChange={(val) => {
                    setChatButtonColor(val);
                    dispatch(chatGPTReducer.updateChatButtonColor(val));
                  }}
                  updateInstant={true}
                  style={{
                    marginLeft: "12px"
                  }}
                  inputClassName={styles.colorPickerInput}
                />
              </div>
              <div className={styles.darkModeToggleWrapper}>
                <LabeledToggle
                  style={{
                    display: "block"
                  }}
                  checked={chatButtonDarkMode}
                  onChange={() => {
                    if (chatButtonDarkMode !== true) {
                      dispatch(chatGPTReducer.updateChatButtonDarkMode(true));
                    } else {
                      dispatch(chatGPTReducer.updateChatButtonDarkMode(false));
                    }
                  }}
                  isAibleColors
                />
                <div className={styles.darkModeToggleLabel}>{"Reverse Content Color"}</div>
              </div>
              {editChatButtonColor && (
                <div className={styles.colorPickerWrapper + " colorPickerWrapper"}>
                  <div className={styles.colorPickerLabel}>
                    {"Custom Color"}
                    <a className={styles.colorPickerClose} onClick={() => closeAllColorPickers()}>
                      {"✕"}
                    </a>
                  </div>
                  <HexColorPicker
                    color={chatButtonColor}
                    onChange={(val) => {
                      setChatButtonColor(val);
                      dispatch(chatGPTReducer.updateChatButtonColor(val));
                    }}
                  />
                </div>
              )}
            </div>

            <div className={styles.colorSetting}>
              <div
                className={styles.colorTop}
                onClick={() => {
                  closeAllColorPickers();
                  setEditBackgroundColor(!editBackgroundColor);
                }}
              >
                <div className={styles.colorSubTitle}>{"Background Color"}</div>
                <div className={styles.showColor} style={{ backgroundColor: backgroundColor }} />
                <LineInput
                  value={backgroundColor}
                  onChange={(val) => {
                    setBackgroundColor(val);
                    dispatch(chatGPTReducer.updateBackgroundColor(val));
                  }}
                  updateInstant={true}
                  style={{
                    marginLeft: "12px"
                  }}
                  inputClassName={styles.colorPickerInput}
                />
              </div>
              <div className={styles.darkModeToggleWrapper}>
                <LabeledToggle
                  style={{
                    display: "block"
                  }}
                  checked={backgroundColorDarkMode}
                  onChange={() => {
                    if (backgroundColorDarkMode !== true) {
                      dispatch(chatGPTReducer.updateBackgroundColorDarkMode(true));
                    } else {
                      dispatch(chatGPTReducer.updateBackgroundColorDarkMode(false));
                    }
                  }}
                  isAibleColors
                />
                <div className={styles.darkModeToggleLabel}>{"Reverse Content Color"}</div>
              </div>
              {editBackgroundColor && (
                <div className={styles.colorPickerWrapper + " colorPickerWrapper"}>
                  <div className={styles.colorPickerLabel}>
                    {"Custom Color"}
                    <a className={styles.colorPickerClose} onClick={() => closeAllColorPickers()}>
                      {"✕"}
                    </a>
                  </div>
                  <HexColorPicker
                    color={backgroundColor}
                    onChange={(val) => {
                      setBackgroundColor(val);
                      dispatch(chatGPTReducer.updateBackgroundColor(val));
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div>
        {renderStep(steps?.[stepIndex], stepIndex)}
        {/* <div className={styles.chatStepSelector}>
              <div className={styles.stepTitle}>{"Step Select"}</div>
              <ButtonGroup>
                <OverlayTrigger
                  placement={"top"}
                  trigger={["hover"]}
                  rootClose
                  overlay={
                    <Popover id={"popover-positioned-top"}>
                      {"Add Step"}
                    </Popover>
                  }
                >
                  <Button
                    style={{ padding: "3px" }}
                    onClick={() => {
                      setSteps({
                        ...steps,
                        [Object.keys(steps).length]: {
                          ...steps[stepIndex],
                        },
                      });
                      setStepIndex(Object.keys(steps).length);
                    }}
                  >
                    {"+"}
                  </Button>
                </OverlayTrigger>
                {Object.keys(steps).map((sIndex) => {
                  return steps?.[sIndex]?.isDeleted === true ? (
                    <div />
                  ) : (
                    <Button
                      key={sIndex}
                      style={{ padding: "3px" }}
                      active={stepIndex + "" === sIndex}
                      onClick={() => {
                        setStepIndex(sIndex);
                      }
                    >
                      {sIndex + ""}
                    </Button>
                  );
                })}
              </ButtonGroup>
            </div> */}
      </div>
    </>
  );
};

export default ChatAppEdit;
